import { zodResolver } from '@hookform/resolvers/zod';
import {
    ianaTimeZoneSchema,
    lengthDisplayUnitSchema,
    massDisplayUnitSchema,
    personNameSchema,
    volumeDisplayUnitSchema,
} from '@baby-tracker/validation';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import fieldStyles from '../../ui/fields.module.css';
import { SelectField } from '../../ui/SelectField.js';
import { TextField } from '../../ui/TextField.js';
import { useFamily, useUpdateFamily } from '../../../hooks/useFamily.js';

const schema = z.object({
    name: personNameSchema,
    timeZone: ianaTimeZoneSchema,
    unitVolume: volumeDisplayUnitSchema,
    unitMass: massDisplayUnitSchema,
    unitLength: lengthDisplayUnitSchema,
});

type Values = z.infer<typeof schema>;

const volumeOptions = [
    { value: 'ml', label: 'Millilitres' },
    { value: 'oz', label: 'Ounces' },
] as const;
const massOptions = [
    { value: 'kg', label: 'Kilograms' },
    { value: 'lb', label: 'Pounds' },
] as const;
const lengthOptions = [
    { value: 'cm', label: 'Centimetres' },
    { value: 'in', label: 'Inches' },
] as const;

export function FamilySection({
    familyId,
    canEdit,
}: {
    familyId: string;
    canEdit: boolean;
}): ReactElement {
    const family = useFamily(familyId);
    const update = useUpdateFamily(familyId);
    const [serverError, setServerError] = useState<string | null>(null);
    const form = useForm<Values>({
        resolver: zodResolver(schema),
        defaultValues: {
            name: '',
            timeZone: 'Australia/Brisbane',
            unitVolume: 'ml',
            unitMass: 'kg',
            unitLength: 'cm',
        },
    });

    useEffect(() => {
        if (family.data) {
            form.reset({
                name: family.data.name,
                timeZone: family.data.timeZone,
                unitVolume: family.data.unitVolume,
                unitMass: family.data.unitMass,
                unitLength: family.data.unitLength,
            });
        }
    }, [family.data, form]);

    async function onSubmit(values: Values): Promise<void> {
        setServerError(null);
        try {
            await update.mutateAsync(values);
        } catch (error) {
            setServerError(errorText(error));
        }
    }

    return (
        <Card title="Family">
            {family.isPending ? <p>Loading family…</p> : null}
            {family.isError ? <FormError message={errorText(family.error)} /> : null}
            {family.data ? (
                <form
                    className={fieldStyles.stack}
                    noValidate
                    onSubmit={(event) => {
                        void form.handleSubmit(onSubmit)(event);
                    }}
                >
                    <TextField
                        label="Name"
                        disabled={!canEdit}
                        error={form.formState.errors.name?.message}
                        {...form.register('name')}
                    />
                    <TextField
                        label="Time zone"
                        disabled={!canEdit}
                        error={form.formState.errors.timeZone?.message}
                        {...form.register('timeZone')}
                    />
                    <SelectField
                        label="Volume"
                        options={volumeOptions}
                        disabled={!canEdit}
                        {...form.register('unitVolume')}
                    />
                    <SelectField
                        label="Weight"
                        options={massOptions}
                        disabled={!canEdit}
                        {...form.register('unitMass')}
                    />
                    <SelectField
                        label="Length"
                        options={lengthOptions}
                        disabled={!canEdit}
                        {...form.register('unitLength')}
                    />
                    <FormError message={serverError} />
                    {canEdit ? (
                        <Button type="submit" loading={form.formState.isSubmitting}>
                            Save family
                        </Button>
                    ) : (
                        <p>Only an owner can change family settings.</p>
                    )}
                </form>
            ) : null}
        </Card>
    );
}
